/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import type { SchedulerCrew } from '@/types/scheduler';

export type CrewMemberFilter = 'all' | 'with-members' | 'empty';

export interface CrewListFilters {
  q: string;
  memberStatus: CrewMemberFilter;
  timezone: string;
  sort: number;
}

export type CrewSortOption = {
  label: string;
  sortBy: 'createdAt' | 'updatedAt' | 'name' | 'members';
  sortDir: 'asc' | 'desc';
  icon: ReactNode;
};

export const DEFAULT_CREW_LIST_FILTERS: CrewListFilters = {
  q: '',
  memberStatus: 'all',
  timezone: '',
  sort: 0,
};

export const CREW_MEMBER_FILTERS: Array<{ value: CrewMemberFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'with-members', label: 'With members' },
  { value: 'empty', label: 'Empty' },
];

export const CREW_SORT_OPTIONS: CrewSortOption[] = [
  { label: 'Created', sortBy: 'createdAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Created', sortBy: 'createdAt', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Updated', sortBy: 'updatedAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Name', sortBy: 'name', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Members', sortBy: 'members', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Members', sortBy: 'members', sortDir: 'asc', icon: <ArrowUp size={12} /> },
];

export function resolveCrewListFilters(search: Partial<CrewListFilters>): CrewListFilters {
  return {
    q: search.q ?? DEFAULT_CREW_LIST_FILTERS.q,
    memberStatus: search.memberStatus ?? DEFAULT_CREW_LIST_FILTERS.memberStatus,
    timezone: search.timezone ?? DEFAULT_CREW_LIST_FILTERS.timezone,
    sort: search.sort ?? DEFAULT_CREW_LIST_FILTERS.sort,
  };
}

export function crewMatchesFilters(crew: SchedulerCrew, filters: CrewListFilters): boolean {
  if (filters.memberStatus === 'with-members' && crew.technicians.length === 0) return false;
  if (filters.memberStatus === 'empty' && crew.technicians.length > 0) return false;
  if (filters.timezone && crew.timezone !== filters.timezone) return false;

  const q = filters.q.trim().toLowerCase();
  if (!q) return true;

  return (
    crew.name.toLowerCase().includes(q) ||
    crew.timezone.toLowerCase().includes(q) ||
    crew.technicians.some(
      (technician) =>
        technician.name.toLowerCase().includes(q) || technician.email.toLowerCase().includes(q),
    )
  );
}

export function sortCrews(crews: SchedulerCrew[], option: CrewSortOption): SchedulerCrew[] {
  return [...crews].sort((a, b) => {
    const left = getSortValue(a, option.sortBy);
    const right = getSortValue(b, option.sortBy);
    const comparison =
      typeof left === 'number' && typeof right === 'number'
        ? left - right
        : String(left).localeCompare(String(right));
    return option.sortDir === 'asc' ? comparison : -comparison;
  });
}

function getSortValue(crew: SchedulerCrew, sortBy: CrewSortOption['sortBy']) {
  if (sortBy === 'members') return crew.technicians.length;
  return crew[sortBy];
}

export function extractCrewTimezones(crews: SchedulerCrew[]): string[] {
  return [...new Set(crews.map((crew) => crew.timezone).filter(Boolean))].sort();
}

export function formatTimezone(timezone: string) {
  return timezone.replaceAll('_', ' ');
}

export function getMemberSummary(crew: SchedulerCrew) {
  const count = crew.technicians.length;
  if (count === 0) return 'No members';
  return `${count} member${count === 1 ? '' : 's'}`;
}
